import type {
  SponsorDto,
  SponsoringOfferDto,
  SponsoringSummaryDto,
  SponsorRequestDto,
  SponsorshipDto,
} from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { scopeQuery, useScope } from '../lib/scope';
import {
  BENEFIT_TYPE_LABELS,
  SPONSORSHIP_STATUS_LABELS,
  SPONSOR_STATUS_LABELS,
  formatCurrency,
  formatDate,
  fromDateInput,
  toDateInput,
} from '../lib/format';
import { Amount } from '../components/Amount';
import { OrgUnitPicker } from '../components/OrgUnitPicker';
import { PageHeader } from '../components/PageHeader';
import { CategoriesDialog, OfferDialog } from '../components/sponsoring/CatalogDialogs';
import {
  FurtherRequestsBadge,
  OfferAvailability,
  SponsorLogo,
  requestSummary,
  useSponsorCategories,
  useSponsoringOffers,
} from '../components/sponsoring/common';
import { RequestDialog } from '../components/sponsoring/RequestDialog';
import { RequestList } from '../components/sponsoring/RequestList';
import { SponsorDialog } from '../components/sponsoring/SponsorDialog';
import { ScopedEmptyState } from '../components/ScopedEmptyState';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Tabs,
  Textarea,
  useToast,
} from '../components/ui';

type TabId = 'sponsors' | 'offers' | 'requests' | 'contracts';

/**
 * Sponsoring aus Vereinssicht.
 *
 * Fuer alle: wer ist Sponsor, wer betreut ihn, wofuer kann angefragt werden
 * und wer hat bereits angefragt. Vertraege und Betraege bleiben vertraulich.
 */
export function SponsoringPage(): JSX.Element {
  const { can } = useAuth();
  const mayContracts = can('sponsoring_contracts', 'read');
  const mayRequest = can('sponsoring_requests', 'create');
  const mayEdit = can('sponsoring', 'update');
  const [tab, setTab] = useState<TabId>('sponsors');
  const [sponsorDialog, setSponsorDialog] = useState(false);
  const [contractDialog, setContractDialog] = useState(false);
  const [requestDialog, setRequestDialog] = useState<{ offerId?: string } | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const navigate = useNavigate();

  const sponsors = useQuery({
    queryKey: ['sponsors'],
    queryFn: () => api.get<SponsorDto[]>('/api/sponsors'),
  });
  const openRequests = useQuery({
    queryKey: ['sponsor-requests', 'open-count'],
    queryFn: () => api.get<SponsorRequestDto[]>('/api/sponsor-requests', { status: ['PLANNED', 'REQUESTED'] }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sponsoring"
        description="Wer unterstützt uns, wer betreut, wofür kann angefragt werden"
        actions={
          <>
            {mayEdit && (
              <Button variant="ghost" onClick={() => setCategoriesOpen(true)}>Kategorien</Button>
            )}
            {mayRequest && (
              <Button variant="secondary" onClick={() => setRequestDialog({})}>Sponsor anfragen</Button>
            )}
            {can('sponsoring', 'create') && (
              <Button variant="secondary" onClick={() => setSponsorDialog(true)}>+ Sponsor</Button>
            )}
            {can('sponsoring_contracts', 'create') && (
              <Button onClick={() => setContractDialog(true)}>+ Vertrag</Button>
            )}
          </>
        }
      />

      <Tabs
        tabs={[
          { id: 'sponsors', label: 'Sponsoren', badge: sponsors.data?.length },
          { id: 'offers', label: 'Werbemöglichkeiten' },
          { id: 'requests', label: 'Anfragen', badge: openRequests.data?.length || undefined },
          { id: 'contracts', label: mayContracts ? 'Verträge' : 'Unterstützungen' },
        ]}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      {tab === 'sponsors' && (
        <SponsorsTab sponsors={sponsors.data} loading={sponsors.isLoading} error={sponsors.error} />
      )}
      {tab === 'offers' && <OffersTab onRequest={(offerId) => setRequestDialog({ offerId })} />}
      {tab === 'requests' && <RequestsTab />}
      {tab === 'contracts' && <ContractsTab />}

      {sponsorDialog && (
        <SponsorDialog
          sponsor={null}
          onClose={() => setSponsorDialog(false)}
          onSaved={(id) => navigate(`/sponsoring/${id}`)}
        />
      )}
      {requestDialog && (
        <RequestDialog
          sponsors={sponsors.data ?? []}
          offerId={requestDialog.offerId}
          onClose={() => setRequestDialog(null)}
        />
      )}
      {categoriesOpen && <CategoriesDialog onClose={() => setCategoriesOpen(false)} />}
      <ContractDialog
        open={contractDialog}
        onClose={() => setContractDialog(false)}
        sponsors={sponsors.data ?? []}
      />
    </div>
  );
}

/* ----------------------------------------------------------------- Sponsoren */

function SponsorsTab({
  sponsors,
  loading,
  error,
}: {
  sponsors: SponsorDto[] | undefined;
  loading: boolean;
  error: unknown;
}): JSX.Element {
  const categories = useSponsorCategories();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [onlyRequestable, setOnlyRequestable] = useState(false);
  const [showEnded, setShowEnded] = useState(false);

  const term = search.trim().toLocaleLowerCase('de');
  const filtered = (sponsors ?? []).filter((sponsor) => {
    if (!showEnded && sponsor.status === 'ENDED') return false;
    if (categoryId === 'none' ? sponsor.categoryId !== null : categoryId && sponsor.categoryId !== categoryId) return false;
    if (onlyRequestable && sponsor.furtherRequests === 'NO') return false;
    if (!term) return true;
    return [sponsor.companyName, sponsor.managedByName, sponsor.categoryName, sponsor.openFor,
      ...sponsor.supportedUnits.map((u) => u.name), ...sponsor.interestOffers.map((o) => o.title)]
      .filter(Boolean).join(' ').toLocaleLowerCase('de').includes(term);
  });

  // Nach Kategorie gruppiert, in der gepflegten Reihenfolge; ohne Kategorie zuletzt.
  const order = new Map((categories.data ?? []).map((c, index) => [c.id, index]));
  const groups = new Map<string, { title: string; rank: number; items: SponsorDto[] }>();
  for (const sponsor of filtered) {
    const key = sponsor.categoryId ?? 'none';
    const group = groups.get(key) ?? {
      title: sponsor.categoryName ?? 'Ohne Kategorie',
      rank: sponsor.categoryId ? (order.get(sponsor.categoryId) ?? 999) : 1000,
      items: [],
    };
    group.items.push(sponsor);
    groups.set(key, group);
  }
  const sorted = [...groups.values()].sort((a, b) => a.rank - b.rank);
  const grouped = sorted.length > 1 || (sorted[0] && sorted[0].title !== 'Ohne Kategorie');

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sponsor, Betreuung, Bereich oder Werbemöglichkeit suchen …"
              aria-label="Sponsoren suchen"
              className="min-w-0 flex-1"
            />
            {(categories.data?.length ?? 0) > 0 && (
              <Select
                aria-label="Kategorie"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-auto"
              >
                <option value="">Alle Kategorien</option>
                {(categories.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="none">Ohne Kategorie</option>
              </Select>
            )}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Checkbox label="Nur anfragbare" checked={onlyRequestable} onChange={setOnlyRequestable} />
            <Checkbox label="Beendete anzeigen" checked={showEnded} onChange={setShowEnded} />
          </div>
        </CardBody>
      </Card>

      {loading && <LoadingBlock />}
      {Boolean(error) && <ErrorBlock error={error} />}
      {sponsors && filtered.length === 0 && (
        <EmptyState title={sponsors.length === 0 ? 'Keine Sponsoren erfasst' : 'Kein Sponsor passt zur Suche'} />
      )}

      {sorted.map((group) => (
        <section key={group.title} className="space-y-2">
          {grouped && (
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-500">
              {group.title}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {group.items.length}
              </span>
            </h2>
          )}
          <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {group.items.map((sponsor) => <SponsorCard key={sponsor.id} sponsor={sponsor} />)}
          </ul>
        </section>
      ))}
    </div>
  );
}

function SponsorCard({ sponsor }: { sponsor: SponsorDto }): JSX.Element {
  return (
    <li>
      <Link to={`/sponsoring/${sponsor.id}`} className="block h-full">
        <Card className="h-full transition-colors hover:border-brand-300">
          <CardBody className="flex h-full flex-col gap-3">
            <div className="flex items-start gap-3">
              <SponsorLogo name={sponsor.companyName} url={sponsor.logoUrl} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">{sponsor.companyName}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {sponsor.categoryName && <Badge tone="brand">{sponsor.categoryName}</Badge>}
                  {sponsor.status !== 'ACTIVE' && (
                    <Badge>{SPONSOR_STATUS_LABELS[sponsor.status] ?? sponsor.status}</Badge>
                  )}
                </div>
              </div>
            </div>
            <FurtherRequestsBadge value={sponsor.furtherRequests} />
            <dl className="space-y-1 text-sm">
              <div>
                <dt className="inline text-slate-500">Betreut von: </dt>
                <dd className="inline text-slate-700">{sponsor.managedByName ?? '–'}</dd>
              </div>
              {sponsor.supportedUnits.length > 0 && (
                <div>
                  <dt className="inline text-slate-500">Unterstützt: </dt>
                  <dd className="inline text-slate-700">{sponsor.supportedUnits.map((u) => u.name).join(', ')}</dd>
                </div>
              )}
              {sponsor.interestOffers.length > 0 && (
                <div>
                  <dt className="inline text-slate-500">Ansprechbar für: </dt>
                  <dd className="inline text-slate-700">{sponsor.interestOffers.map((o) => o.title).join(', ')}</dd>
                </div>
              )}
            </dl>
            <p className="mt-auto text-xs text-slate-500">
              {sponsor.openRequests > 0
                ? `⏳ ${sponsor.openRequests} laufende Anfrage(n)`
                : sponsor.lastRequest
                  ? `Zuletzt: ${requestSummary(sponsor.lastRequest)}`
                  : 'Noch keine Anfrage erfasst'}
            </p>
          </CardBody>
        </Card>
      </Link>
    </li>
  );
}

/* -------------------------------------------------------- Werbemoeglichkeiten */

function OffersTab({ onRequest }: { onRequest: (offerId: string) => void }): JSX.Element {
  const { can } = useAuth();
  const mayEdit = can('sponsoring', 'update');
  const mayRequest = can('sponsoring_requests', 'create');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<SponsoringOfferDto | 'neu' | null>(null);
  const offers = useSponsoringOffers(showArchived);

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">
            Was der Verein Sponsoren anbieten kann – mit aktueller Belegung aus den laufenden Verträgen.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {mayEdit && <Checkbox label="Archivierte anzeigen" checked={showArchived} onChange={setShowArchived} />}
            {mayEdit && <Button size="sm" onClick={() => setEditing('neu')}>+ Werbemöglichkeit</Button>}
          </div>
        </CardBody>
      </Card>

      {offers.isLoading && <LoadingBlock />}
      {offers.error && <ErrorBlock error={offers.error} />}
      {offers.data && offers.data.length === 0 && (
        <EmptyState
          title="Noch keine Werbemöglichkeiten"
          description={mayEdit ? 'Lege an, was der Verein anbieten kann – z. B. Banden, Trikots oder Matchsponsoring.' : undefined}
        />
      )}

      <ul className="grid gap-3 md:grid-cols-2">
        {(offers.data ?? []).map((offer) => (
          <li key={offer.id}>
            <Card className={offer.archived ? 'opacity-60' : undefined}>
              <CardBody className="space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">{offer.title}</p>
                    <p className="text-xs text-slate-500">
                      {[offer.organisationUnit?.name ?? 'Ganzer Verein',
                        offer.benefitType ? BENEFIT_TYPE_LABELS[offer.benefitType] : null].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {offer.archived && <Badge>archiviert</Badge>}
                    <OfferAvailability offer={offer} />
                  </div>
                </div>
                {offer.description && <p className="text-sm text-slate-600">{offer.description}</p>}
                {offer.occupiedBy.length > 0 && (
                  <p className="text-sm">
                    <span className="text-slate-500">Belegt durch: </span>
                    {offer.occupiedBy.map((o, index) => (
                      <span key={`${o.sponsorId}-${index}`}>
                        {index > 0 && ', '}
                        <Link to={`/sponsoring/${o.sponsorId}`} className="text-brand-700 hover:underline">{o.sponsorName}</Link>
                        <span className="text-slate-500"> (bis {formatDate(o.validUntil)})</span>
                      </span>
                    ))}
                  </p>
                )}
                {offer.interestedSponsors.length > 0 && (
                  <p className="text-sm">
                    <span className="text-slate-500">Ansprechbar: </span>
                    {offer.interestedSponsors.map((s, index) => (
                      <span key={s.id}>
                        {index > 0 && ', '}
                        <Link to={`/sponsoring/${s.id}`} className="text-brand-700 hover:underline">{s.name}</Link>
                      </span>
                    ))}
                  </p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <span className="text-xs text-slate-500">
                    {offer.openRequests > 0 ? `⏳ ${offer.openRequests} laufende Anfrage(n)` : ''}
                  </span>
                  <div className="flex gap-2">
                    {mayEdit && (
                      <Button size="sm" variant="ghost" onClick={() => setEditing(offer)}>Bearbeiten</Button>
                    )}
                    {mayRequest && !offer.archived && (
                      <Button size="sm" variant="secondary" onClick={() => onRequest(offer.id)}>Anfragen</Button>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </li>
        ))}
      </ul>

      {editing && (
        <OfferDialog
          key={editing === 'neu' ? 'neu' : editing.id}
          offer={editing === 'neu' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Anfragen */

function RequestsTab(): JSX.Element {
  const [view, setView] = useState<'open' | 'all' | 'mine'>('open');
  const query = view === 'open'
    ? { status: ['PLANNED', 'REQUESTED'] }
    : view === 'mine' ? { mine: true } : {};
  const requests = useQuery({
    queryKey: ['sponsor-requests', view],
    queryFn: () => api.get<SponsorRequestDto[]>('/api/sponsor-requests', query),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">
            Wer hat wen wann wofür angefragt? Die Betreuung eines Sponsors wird bei neuen Anfragen informiert.
          </p>
          <Select aria-label="Anfragen filtern" value={view} onChange={(e) => setView(e.target.value as typeof view)} className="w-auto">
            <option value="open">Laufende</option>
            <option value="mine">Meine</option>
            <option value="all">Alle</option>
          </Select>
        </CardBody>
      </Card>
      {requests.isLoading && <LoadingBlock />}
      {requests.error && <ErrorBlock error={requests.error} />}
      {requests.data && (
        <Card>
          <CardBody>
            <RequestList
              requests={requests.data}
              emptyText={view === 'open' ? 'Keine laufenden Anfragen.' : 'Keine Anfragen gefunden.'}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Vertraege */

function ContractsTab(): JSX.Element {
  const scope = useScope();
  const { can } = useAuth();
  const mayContracts = can('sponsoring_contracts', 'read');
  const contractQuery = scopeQuery(scope);
  const { data: contracts, isLoading, error } = useQuery({
    queryKey: ['sponsorships', contractQuery],
    queryFn: () => api.get<SponsorshipDto[]>('/api/sponsorships', contractQuery),
  });

  return (
    <>
      {mayContracts ? (
        <SponsoringSummary />
      ) : (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          🔒 Vertragsinhalte und Beträge sind vertraulich. Sichtbar ist, wer welchen Bereich bis wann unterstützt.
        </p>
      )}

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock error={error} />}
      {contracts && contracts.length === 0 && (
        <ScopedEmptyState
          title="Keine Sponsorverträge"
          description="Für den gewählten Bereich sind keine Verträge hinterlegt."
        />
      )}
      <ul className="space-y-2">
        {(contracts ?? []).map((contract) => (
          <li key={contract.id}>
            <Link to={`/sponsoring/vertraege/${contract.id}`}>
              <Card className="transition-colors hover:border-brand-300">
                <CardBody className="space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">{contract.sponsorName}</p>
                      <p className="truncate text-sm text-slate-600">{contract.title}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge
                        tone={contract.status === 'ACTIVE' ? 'success' : contract.status === 'EXPIRING' ? 'warning' : 'neutral'}
                      >
                        {SPONSORSHIP_STATUS_LABELS[contract.status] ?? contract.status}
                      </Badge>
                      {contract.detailsVisible && contract.amountBand && (
                        <Amount
                          amount={contract.amount}
                          band={contract.amountBand}
                          currency={contract.currency}
                          className="text-sm"
                        />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    {formatDate(contract.validFrom)} – {formatDate(contract.validUntil)}
                    {contract.daysUntilEnd >= 0 ? ` · endet in ${contract.daysUntilEnd} Tagen` : ' · abgelaufen'}
                    {' · '}
                    {contract.organisationUnits.map((u) => u.name).join(', ')}
                  </p>
                  {contract.benefits.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {contract.benefits.map((benefit) => (
                        <Badge
                          key={benefit.id}
                          tone={benefit.status === 'DELIVERED' ? 'success' : benefit.status === 'OPEN' ? 'warning' : 'neutral'}
                        >
                          {benefit.status === 'DELIVERED' ? '✓' : '○'}{' '}
                          {benefit.offerTitle ?? BENEFIT_TYPE_LABELS[benefit.type] ?? benefit.type}
                          {benefit.quantity > 1 && ` ${benefit.deliveredCount}/${benefit.quantity}`}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Kennzahlen des Sponsorings.
 * Die Summe erscheint nur, wenn alle Betraege des Bereichs einsehbar sind -
 * sonst liesse sich ein verborgener Einzelbetrag daraus errechnen.
 */
function SponsoringSummary(): JSX.Element | null {
  const scope = useScope();
  const query = scopeQuery(scope);

  const { data } = useQuery({
    queryKey: ['sponsorships', 'summary', query],
    queryFn: () => api.get<SponsoringSummaryDto>('/api/sponsorships/summary', query),
  });

  if (!data || data.activeCount === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Card className="px-4 py-3">
        <p className="text-2xl font-semibold tabular-nums text-slate-900">{data.activeCount}</p>
        <p className="text-xs font-medium text-slate-500">Laufende Verträge</p>
      </Card>
      <Card className="px-4 py-3">
        <p className="text-2xl font-semibold tabular-nums text-slate-900">
          {data.totalAmount === null ? '–' : formatCurrency(data.totalAmount, data.currency)}
        </p>
        <p className="text-xs font-medium text-slate-500">
          {data.totalAmount === null
            ? `Summe verborgen (${data.hiddenAmounts} Verträge)`
            : 'Vereinbarte Summe'}
        </p>
      </Card>
      <Card className="px-4 py-3">
        <p
          className={`text-2xl font-semibold tabular-nums ${
            data.expiringSoon > 0 ? 'text-amber-600' : 'text-slate-900'
          }`}
        >
          {data.expiringSoon}
        </p>
        <p className="text-xs font-medium text-slate-500">Laufen bald aus</p>
      </Card>
      <Card className="px-4 py-3">
        <p
          className={`text-2xl font-semibold tabular-nums ${
            data.overdueBenefits > 0 ? 'text-rose-600' : 'text-slate-900'
          }`}
        >
          {data.openBenefits}
        </p>
        <p className="text-xs font-medium text-slate-500">
          Offene Leistungen
          {data.overdueBenefits > 0 && ` · ${data.overdueBenefits} überfällig`}
        </p>
      </Card>
    </div>
  );
}

/** Leistung im Formular - bestehende tragen ihre id mit. */
interface BenefitDraft {
  id?: string;
  type: string;
  quantity: number;
  deliveredCount: number;
  status: string;
  dueAt: string | null;
  description: string | null;
  offerId: string | null;
}

export function ContractDialog({
  open,
  onClose,
  sponsors,
  sponsorship,
}: {
  open: boolean;
  onClose: () => void;
  sponsors: SponsorDto[];
  /** Gesetzt: bestehenden Vertrag bearbeiten. */
  sponsorship?: SponsorshipDto | null;
}): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const isEdit = !!sponsorship;
  // Ohne Finanzberechtigung wird das Betragsfeld gar nicht erst angeboten.
  const mayEnterAmount = can('finance', isEdit ? 'update' : 'create');
  // Ein verborgener Betrag darf beim Speichern nicht mit 0 ueberschrieben werden.
  const amountHidden = isEdit && sponsorship?.amount === null;

  const [sponsorId, setSponsorId] = useState('');
  const [title, setTitle] = useState('');
  const [validFrom, setValidFrom] = useState(toDateInput(new Date().toISOString()));
  const [validUntil, setValidUntil] = useState('');
  const [amount, setAmount] = useState('0');
  const [status, setStatus] = useState('ACTIVE');
  const [notes, setNotes] = useState('');
  const [unitIds, setUnitIds] = useState<string[]>(scope.unitId ? [scope.unitId] : []);
  const [benefits, setBenefits] = useState<BenefitDraft[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const offers = useSponsoringOffers(true);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (sponsorship) {
      setSponsorId(sponsorship.sponsorId);
      setTitle(sponsorship.title);
      setValidFrom(toDateInput(sponsorship.validFrom));
      setValidUntil(toDateInput(sponsorship.validUntil));
      setAmount(sponsorship.amount === null ? '0' : String(sponsorship.amount));
      setStatus(sponsorship.status);
      setNotes(sponsorship.notes ?? '');
      setUnitIds(sponsorship.organisationUnits.map((u) => u.id));
      setBenefits(
        sponsorship.benefits.map((b) => ({
          id: b.id,
          type: b.type,
          quantity: b.quantity,
          deliveredCount: b.deliveredCount,
          status: b.status,
          dueAt: b.dueAt,
          description: b.description,
          offerId: b.offerId,
        })),
      );
    } else {
      setSponsorId('');
      setTitle('');
      setValidFrom(toDateInput(new Date().toISOString()));
      setValidUntil('');
      setAmount('0');
      setStatus('ACTIVE');
      setNotes('');
      setUnitIds(scope.unitId ? [scope.unitId] : []);
      setBenefits([]);
    }
  }, [open, sponsorship, scope.unitId]);

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        title,
        validFrom: fromDateInput(validFrom),
        validUntil: fromDateInput(validUntil, true),
        status,
        notes: notes || null,
        organisationUnitIds: unitIds,
        benefits: benefits.map((b) => ({
          ...(b.id ? { id: b.id } : {}),
          type: b.type,
          quantity: b.quantity,
          deliveredCount: Math.min(b.deliveredCount, b.quantity),
          status: b.status,
          dueAt: b.dueAt,
          description: b.description,
          offerId: b.offerId,
        })),
      };
      // Betrag nur senden, wenn er auch eingesehen und gepflegt werden darf.
      if (mayEnterAmount && !amountHidden) payload['amount'] = Number(amount);

      return isEdit
        ? api.patch(`/api/sponsorships/${sponsorship?.id}`, payload)
        : api.post('/api/sponsorships', { ...payload, sponsorId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsorships'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
      toast.show(isEdit ? 'Sponsorvertrag gespeichert' : 'Sponsorvertrag angelegt');
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setErrors(err.fieldErrors());
        toast.show(err.message, 'error');
      } else {
        toast.show('Speichern fehlgeschlagen', 'error');
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Sponsorvertrag bearbeiten' : 'Neuer Sponsorvertrag'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!sponsorId || !title || !validUntil || unitIds.length === 0}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!isEdit && (
          <Field label="Sponsor" required>
            {(id) => (
              <Select id={id} value={sponsorId} onChange={(e) => setSponsorId(e.target.value)}>
                <option value="">Bitte wählen …</option>
                {sponsors.map((sponsor) => (
                  <option key={sponsor.id} value={sponsor.id}>
                    {sponsor.companyName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label="Bezeichnung" required error={errors['title']}>
          {(id) => (
            <Input
              id={id}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Hauptsponsor Saison 2027"
            />
          )}
        </Field>
        <div className={mayEnterAmount ? 'grid gap-4 sm:grid-cols-3' : 'grid gap-4 sm:grid-cols-2'}>
          <Field label="Gültig ab" required>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            )}
          </Field>
          <Field label="Gültig bis" required error={errors['validUntil']}>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            )}
          </Field>
          {mayEnterAmount && !amountHidden && (
            <Field label="Betrag (EUR)" error={errors['amount']}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
            </Field>
          )}
        </div>

        {amountHidden && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Der vereinbarte Betrag ist für dich nicht einsehbar und bleibt beim Speichern
            unverändert.
          </p>
        )}

        {isEdit && (
          <Field label="Status">
            {(id) => (
              <Select id={id} value={status} onChange={(e) => setStatus(e.target.value)}>
                {Object.entries(SPONSORSHIP_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <Field label="Bereiche" required error={errors['organisationUnitIds']}>
          {() => <OrgUnitPicker value={unitIds} onChange={setUnitIds} />}
        </Field>

        <Field label="Notizen">
          {(id) => (
            <Textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          )}
        </Field>

        <div>
          <p className="label">Vereinbarte Leistungen</p>
          <div className="space-y-2 rounded-xl border border-slate-300 p-2">
            {benefits.map((benefit, index) => (
              <div key={benefit.id ?? `neu-${index}`} className="flex flex-wrap items-center gap-2">
                <Select
                  value={benefit.type}
                  onChange={(e) =>
                    setBenefits(
                      benefits.map((b, i) => (i === index ? { ...b, type: e.target.value } : b)),
                    )
                  }
                  className="min-w-40 flex-1"
                >
                  {Object.entries(BENEFIT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
                {(offers.data?.length ?? 0) > 0 && (
                  <Select
                    aria-label="Werbemöglichkeit"
                    value={benefit.offerId ?? ''}
                    onChange={(e) => {
                      const offer = offers.data?.find((o) => o.id === e.target.value);
                      setBenefits(benefits.map((b, i) => (i === index
                        ? { ...b, offerId: offer?.id ?? null, type: offer?.benefitType ?? b.type }
                        : b)));
                    }}
                    className="min-w-40 flex-1"
                  >
                    <option value="">– ohne Werbemöglichkeit –</option>
                    {(offers.data ?? [])
                      .filter((o) => !o.archived || o.id === benefit.offerId)
                      .map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.title}{o.free === 0 && o.id !== benefit.offerId ? ' (vergeben)' : ''}
                        </option>
                      ))}
                  </Select>
                )}
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  Anzahl
                  <Input
                    type="number"
                    min="1"
                    value={benefit.quantity}
                    onChange={(e) =>
                      setBenefits(
                        benefits.map((b, i) =>
                          i === index ? { ...b, quantity: Math.max(1, Number(e.target.value)) } : b,
                        ),
                      )
                    }
                    className="w-20"
                  />
                </label>
                {benefit.id && benefit.deliveredCount > 0 && (
                  <Badge tone="info">{benefit.deliveredCount} erbracht</Badge>
                )}
                <button
                  type="button"
                  className="text-sm text-slate-400 hover:text-rose-600"
                  onClick={() => setBenefits(benefits.filter((_, i) => i !== index))}
                >
                  entfernen
                </button>
                <Input
                  aria-label="Hinweis zur Leistung"
                  placeholder="Hinweis, z. B. „Bande Nr. 7, neben dem Tor“"
                  maxLength={500}
                  value={benefit.description ?? ''}
                  onChange={(e) =>
                    setBenefits(
                      benefits.map((b, i) => (i === index ? { ...b, description: e.target.value || null } : b)),
                    )
                  }
                  className="w-full basis-full text-sm"
                />
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setBenefits([
                  ...benefits,
                  {
                    type: 'BANDENWERBUNG',
                    quantity: 1,
                    deliveredCount: 0,
                    status: 'OPEN',
                    dueAt: null,
                    description: null,
                    offerId: null,
                  },
                ])
              }
            >
              + Leistung
            </Button>
          </div>
          {isEdit && (
            <p className="hint">
              Bereits erbrachte Leistungen bleiben erhalten. Entfernte Zeilen werden gelöscht.
            </p>
          )}
        </div>
      </div>
    </Dialog>
  );
}
